// Sugalarm — mobile background layer (cycle 171, step 1).
//
// Generalizes the cycle-164 inline `.aurora` layer into one component
// that renders any of the 18 built-in presets, read from the settings
// context (`display_mobile_background`, default "aurora"). Mounted as the
// backmost child of both MobileFrame branches (portrait + landscape). It
// keeps every cycle-164 invariant: absolute/inset-0/z-index:-1/
// pointer-events:none/aria-hidden, behind all content, STATIC (no
// animation), mobile-only by component scope. All color is token-driven
// from the `[data-bg]` blocks in tokens.css (set on the mobile root by
// applyAppearance); this file carries geometry only, no hex.

import { useEffect, useState } from "react";

import { useSettings } from "../../hooks/useSettings";
import { effectiveBg, isImage, isScene } from "../../lib/mobileBackgrounds";
import { useThemePacks } from "../../lib/themePacksContext";
import type { MobileBackground as BgId } from "../../lib/types";
import styles from "./MobileBackground.module.css";

// The shared blurred ridgeline (identical in the vista + heroic mockups),
// 4 paths back→front; per-preset fills come from --mbg-r1..4.
const HILLS = [
  "M0 170 C 60 140 110 196 170 166 S 260 150 300 176 L300 360 L0 360 Z",
  "M0 222 C 70 192 120 246 182 216 S 272 206 300 226 L300 360 L0 360 Z",
  "M0 266 C 62 240 132 292 202 263 S 282 256 300 271 L300 360 L0 360 Z",
  "M0 306 C 82 286 152 326 222 301 S 286 299 300 309 L300 360 L0 360 Z",
];

// Cycle 164 aurora ground — the no-op default AND the universal image
// fallback (offline / CDN down / no art uploaded). Never blank/black.
function AuroraLayer() {
  return <div className={`${styles.layer} ${styles.aurora}`} aria-hidden="true" />;
}

// Cycle 5 · image preset — a fixed, cover-fit hosted WebP behind a
// legibility scrim. Preloads the chosen src and keeps the aurora fallback
// showing until it's ready (no flash of empty); falls back to aurora on
// error or when no src is configured (the pack art isn't uploaded yet).
function ImageLayer({ src }: { src: string | undefined }) {
  const [readySrc, setReadySrc] = useState<string | null>(null);

  useEffect(() => {
    if (!src) {
      setReadySrc(null);
      return;
    }
    let alive = true;
    const img = new Image();
    img.onload = () => {
      if (alive) setReadySrc(src);
    };
    img.onerror = () => {
      if (alive) setReadySrc(null);
    };
    img.src = src;
    return () => {
      alive = false;
    };
  }, [src]);

  // Not loaded (yet) / errored / no src → the aurora ground, never blank.
  if (!readySrc) return <AuroraLayer />;

  return (
    <div className={`${styles.layer} ${styles.image}`} aria-hidden="true">
      <div
        className={styles.imageGround}
        style={{ backgroundImage: `url("${readySrc}")` }}
      />
      {/* Cycle 344: was `.scrim` — that class reads var(--mbg-scrim),
          defined only per curated SCENE preset. No `image`-kind background
          (the 3 relic-* packs, or any uploaded photo) ever had that token
          set, so the reference was invalid and painted nothing — confirmed
          on a real device photo. `.imageScrim` is the fixed, always-on
          replacement; see its doc comment in the CSS module. */}
      <div className={styles.imageScrim} />
    </div>
  );
}

export function MobileBackground() {
  const { settings } = useSettings();
  // Cycle 174: render the EFFECTIVE preset (premium falls back to aurora
  // when the pack isn't owned) — matches the data-bg applyAppearance sets.
  const { ownedPackIds, imageSrcOf, packOf, loaded, offeredPresetIds } =
    useThemePacks();
  const preset: BgId = effectiveBg(
    settings?.display_mobile_background ?? "aurora",
    ownedPackIds,
    packOf,
    { loaded, offered: offeredPresetIds },
  );

  // Default / unset → the cycle-164 aurora, byte-identical.
  if (preset === "aurora") return <AuroraLayer />;

  // Cycle 5 / 189: image preset — the static `relic-*` presets OR any
  // runtime-uploaded image preset (detected by having resolved srcs in the
  // catalog). Pick light/dark by the active mode, then preload with an
  // aurora fallback.
  const srcs = imageSrcOf(preset);
  if (isImage(preset) || srcs) {
    const dark = settings?.display_dark_mode ?? false;
    const src = srcs ? (dark ? srcs.dark_src : srcs.light_src) : undefined;
    return <ImageLayer src={src} />;
  }

  const scene = isScene(preset);
  return (
    <div className={`${styles.layer} ${styles.base}`} aria-hidden="true">
      {preset === "contour" ? <ContourSvg /> : null}
      {preset === "dot-grid" ? <div className={styles.dots} /> : null}
      {scene ? (
        <>
          <Ridge preset={preset} />
          <Decorations preset={preset} />
          <div className={styles.scrim} />
        </>
      ) : null}
    </div>
  );
}

// ── Pattern · Contour — faint topographic line-art (stroke = --mbg-line). ──
function ContourSvg() {
  return (
    <svg
      className={styles.contour}
      viewBox="0 0 400 800"
      preserveAspectRatio="none"
      aria-hidden
    >
      <g fill="none" stroke="currentColor" strokeWidth="1.4" opacity="0.9">
        <path d="M-20 120 C 120 60 260 180 440 110" />
        <path d="M-20 180 C 120 120 260 240 440 170" />
        <path d="M-20 240 C 120 180 260 300 440 230" />
        <path d="M-20 360 C 120 300 280 420 440 350" />
        <path d="M-20 430 C 120 370 280 490 440 420" />
        <path d="M-20 560 C 130 500 280 620 440 550" />
        <path d="M-20 630 C 130 570 280 690 440 620" />
        <path d="M-20 700 C 130 640 280 760 440 690" />
      </g>
    </svg>
  );
}

// ── Scene · ridgeline (+ distant spire / keep silhouette in the back fill). ──
function Ridge({ preset }: { preset: BgId }) {
  return (
    <svg
      className={styles.ridge}
      viewBox="0 0 300 360"
      preserveAspectRatio="none"
      aria-hidden
    >
      {HILLS.map((d, i) => (
        <path key={i} d={d} fill={`var(--mbg-r${i + 1})`} />
      ))}
      {preset === "heros-horizon" ? <g fill="var(--mbg-r1)">{spire(196)}</g> : null}
      {preset === "twilight-keep" ? <g fill="var(--mbg-r1)">{keep(130)}</g> : null}
    </svg>
  );
}

function spire(x: number) {
  return (
    <>
      <path d={`M${x} 168 L${x + 4} 150 L${x + 8} 168 Z`} />
      <rect x={x + 1} y={166} width={6} height={22} />
      <path d={`M${x - 9} 176 L${x - 6} 164 L${x - 3} 176 Z`} />
      <rect x={x - 8} y={174} width={5} height={14} />
      <path d={`M${x + 11} 178 L${x + 14} 168 L${x + 17} 178 Z`} />
      <rect x={x + 11} y={176} width={5} height={12} />
    </>
  );
}
function keep(x: number) {
  return (
    <>
      <rect x={x} y={196} width={40} height={26} />
      <rect x={x + 15} y={170} width={11} height={52} />
      <path d={`M${x + 13} 170 L${x + 20.5} 156 L${x + 28} 170 Z`} />
      <rect x={x} y={192} width={5} height={6} />
      <rect x={x + 9} y={192} width={5} height={6} />
      <rect x={x + 27} y={192} width={5} height={6} />
      <rect x={x + 35} y={192} width={5} height={6} />
    </>
  );
}

// ── Scene · per-preset decorations (rays / embers / spores / crest / moon). ──
function Decorations({ preset }: { preset: BgId }) {
  if (preset === "heros-horizon") {
    return (
      <>
        <Emblem />
        <Rays angles={[11, -8]} />
        <Motes points={MOTES_WARM} />
      </>
    );
  }
  if (preset === "sacred-glade") {
    return (
      <>
        <Rays angles={[11, -8]} />
        <Motes points={MOTES_GLADE} />
      </>
    );
  }
  if (preset === "twilight-keep") {
    return (
      <>
        <div className={styles.moon} />
        <Motes points={MOTES_KEEP} />
      </>
    );
  }
  // Vista scenes (golden-plateau / dawn-mist / verdant-calm): sky + ridges
  // + scrim only, no decorations.
  return null;
}

function Rays({ angles }: { angles: number[] }) {
  return (
    <>
      {angles.map((deg, i) => (
        <div
          key={i}
          className={styles.ray}
          style={{
            left: i === 0 ? "30%" : "58%",
            width: i === 0 ? 46 : 36,
            height: i === 0 ? 300 : 280,
            background: "linear-gradient(var(--mbg-ray), transparent)",
            transform: `rotate(${deg}deg)`,
            opacity: i === 0 ? 0.5 : 0.4,
          }}
        />
      ))}
    </>
  );
}

type Mote = { l: number; t: number; s: number; o: number };
const MOTES_WARM: Mote[] = [
  { l: 22, t: 40, s: 5, o: 0.8 },
  { l: 70, t: 32, s: 4, o: 0.7 },
  { l: 40, t: 52, s: 3, o: 0.6 },
  { l: 82, t: 46, s: 5, o: 0.6 },
  { l: 14, t: 58, s: 3, o: 0.5 },
];
const MOTES_GLADE: Mote[] = [
  { l: 28, t: 38, s: 4, o: 0.8 },
  { l: 64, t: 30, s: 3, o: 0.7 },
  { l: 46, t: 50, s: 5, o: 0.65 },
  { l: 78, t: 44, s: 3, o: 0.6 },
  { l: 18, t: 56, s: 4, o: 0.5 },
  { l: 54, t: 62, s: 3, o: 0.5 },
];
const MOTES_KEEP: Mote[] = [
  { l: 24, t: 44, s: 3, o: 0.5 },
  { l: 74, t: 40, s: 3, o: 0.5 },
  { l: 44, t: 54, s: 3, o: 0.45 },
];

function Motes({ points }: { points: Mote[] }) {
  return (
    <>
      {points.map((p, i) => (
        <span
          key={i}
          className={styles.mote}
          style={{
            left: `${p.l}%`,
            top: `${p.t}%`,
            width: p.s,
            height: p.s,
            opacity: p.o,
          }}
        />
      ))}
    </>
  );
}

// The invented compass-crest (original art — no trademark): two rings +
// an 8-point star, drawn in --mbg-emblem at low opacity behind the hero.
function Emblem() {
  return (
    <svg
      className={styles.emblem}
      width="120"
      height="120"
      viewBox="0 0 120 120"
      aria-hidden
    >
      <g fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="60" cy="60" r="40" />
        <circle cx="60" cy="60" r="30" />
      </g>
      <path
        d="M60 12 L67 53 L108 60 L67 67 L60 108 L53 67 L12 60 L53 53 Z"
        fill="currentColor"
      />
    </svg>
  );
}
